<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  botId: {
    type: [Number, String],
    required: true,
  },
  rule: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const message = ref('');
const variablesText = ref('{}');
const result = ref(null);
const error = ref('');
const isRunning = ref(false);

const runTest = async () => {
  let variables = {};
  error.value = '';
  try {
    variables = JSON.parse(variablesText.value || '{}');
  } catch {
    error.value = t('AI_AGENT.API_RULES.TEST.INVALID_VARIABLES');
    return;
  }

  isRunning.value = true;
  result.value = null;
  try {
    const data = await store.dispatch('aiBotApiRules/test', {
      botId: props.botId,
      id: props.rule.id,
      message: message.value,
      variables,
    });
    if (data) {
      result.value = data;
    }
  } finally {
    isRunning.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <Input
      v-model="message"
      :label="$t('AI_AGENT.API_RULES.TEST.MESSAGE')"
      :placeholder="$t('AI_AGENT.API_RULES.TEST.MESSAGE_PLACEHOLDER')"
      data-testid="test-message"
      @keyup.enter="runTest"
    />
    <TextArea
      v-model="variablesText"
      :label="$t('AI_AGENT.API_RULES.TEST.VARIABLES')"
      :placeholder="$t('AI_AGENT.API_RULES.TEST.VARIABLES_PLACEHOLDER')"
      :message="$t('AI_AGENT.API_RULES.TEST.VARIABLES_HELP')"
      data-testid="test-variables"
    />
    <p v-if="error" class="text-sm text-n-ruby-11" data-testid="test-error">
      {{ error }}
    </p>
    <div>
      <Button
        :label="$t('AI_AGENT.API_RULES.TEST.RUN')"
        :is-loading="isRunning"
        :disabled="message.trim() === ''"
        data-testid="test-run"
        @click="runTest"
      />
    </div>

    <div v-if="result" class="flex flex-col gap-3" data-testid="test-result">
      <div class="flex items-center gap-3">
        <span
          class="text-xs rounded-md py-0.5 px-2"
          :class="
            result.ok
              ? 'bg-n-green-5 text-n-green-11'
              : 'bg-n-ruby-5 text-n-ruby-11'
          "
        >
          {{
            result.ok
              ? $t('AI_AGENT.API_RULES.TEST.OK')
              : $t('AI_AGENT.API_RULES.TEST.ERROR')
          }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{
            $t('AI_AGENT.API_RULES.TEST.STATUS', {
              status: result.status ?? '—',
            })
          }}
        </span>
      </div>
      <div v-if="result.reply" class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('AI_AGENT.API_RULES.TEST.REPLY') }}
        </span>
        <p class="text-sm whitespace-pre-wrap text-n-slate-11">
          {{ result.reply }}
        </p>
      </div>
      <div v-if="result.debug" class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('AI_AGENT.API_RULES.TEST.DEBUG') }}
        </span>
        <pre
          class="p-2 overflow-x-auto text-xs rounded-md bg-n-solid-2 text-n-slate-11"
          data-testid="test-debug"
          >{{ JSON.stringify(result.debug, null, 2) }}</pre
        >
      </div>
    </div>
  </div>
</template>
